import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/macro';

// codeToMessage maps backend error codes (shared.ErrorResponse.Code) to
// Lingui message descriptors. Keep in sync with
// internal/handlers/shared/error_codes.go.
export const codeToMessage: Record<string, MessageDescriptor> = {
  // Adapt allocations to the contract (capacity)
  ADAPT_NO_ACTIVE_CONTRACT: msg`The child has no active or upcoming contract version.`,
  ADAPT_MULTIPLE_CONTRACT_GROUPS: msg`The child has more than one active contract. Please adapt the allocations by hand.`,
  ADAPT_MIXED_GROUP_TYPES_ON_WEEKDAY: msg`The contract books services of two group types on this day. Please adapt the allocations by hand.`,
  ADAPT_GROUP_CHOICE_REQUIRED: msg`Please choose a group for this day.`,
  ADAPT_END_DATE_REQUIRED: msg`The contract version has no end date. Please choose until when the allocations run.`,
  ADAPT_NO_GROUP_OF_TYPE: msg`There is no group of the contracted type for this day.`,

  // Auth
  AUTH_CONTEXT_MISSING: msg`Your session expired. Please sign in again.`,
  USER_CONTEXT_MISSING: msg`Your session expired. Please sign in again.`,

  // Contract
  CONTRACT_NOT_FOUND: msg`Contract not found`,
  CONTRACT_INVALID_STATE: msg`Invalid contract state`,
  CONTRACT_TERMINATION_BEFORE_START: msg`Termination date cannot be before the start date`,
  CONTRACT_BEFORE_BIRTH_DATE: msg`Contract cannot start before the child was born`,
  CONTRACT_INSUFFICIENT_COVERAGE: msg`Contract must cover at least one day`,
  CONTRACT_BILLING_START_BEFORE: msg`Billing start date cannot be before the contract start`,
  CONTRACT_NO_SERVICES: msg`Contract must have at least one service`,
  CONTRACT_OVERLAP: msg`Contract overlaps with an existing contract`,
  CONTRACT_TERMINAL_WITHOUT_END: msg`An ended contract must have an end date`,
  CONTRACT_PAYERS_OPEN_INVOICES: msg({
    id: 'contracts.payers.error.openInvoices.v2',
    message: 'Shares cannot change while the contract has open invoices. Settle or cancel them first.',
  }),

  // Schedule a contract change: the change must fit inside the selected version.
  SCHEDULE_CHANGE_STARTS_BEFORE_VERSION: msg({
    id: 'contracts.schedule.error.startsBeforeVersion',
    message: 'The change starts before the selected version. Select the version that covers the change date and click "Add" again.',
  }),
  SCHEDULE_CHANGE_OUTSIDE_VERSION_END: msg({
    id: 'contracts.schedule.error.outsideVersionEnd',
    message: 'The change runs past the end of the selected version. End the change sooner, or select the version that covers the change date.',
  }),
  SCHEDULE_CHANGE_VERSION_CLOSED: msg({
    id: 'contracts.schedule.error.versionClosed',
    message: 'This version is cancelled or terminated. Select the active version that covers the change date.',
  }),

  // Termination revoke (issue #1678)
  TERMINATION_REVOKE_CHILD_LEFT: msg({
    id: 'termination.revoke.error.childLeft',
    message: 'The child has left; create a new contract instead',
  }),
  TERMINATION_REVOKE_NOT_TERMINATED: msg({
    id: 'termination.revoke.error.notTerminated',
    message: 'This contract is not terminated',
  }),
  TERMINATION_REVOKE_END_DATE_REQUIRED: msg({
    id: 'termination.revoke.error.endDateRequired',
    message: 'Enter the date the contract should now end',
  }),
  TERMINATION_REVOKE_END_DATE_IN_PAST: msg({
    id: 'termination.revoke.error.endDateInPast',
    message: 'The new end date must not be in the past',
  }),
  TERMINATION_REVOKE_END_DATE_INVALID: msg({
    id: 'termination.revoke.error.endDateInvalid',
    message: 'The new end date is before the contract starts',
  }),
  TERMINATION_REVOKE_BLOCKED_BY_INVOICES: msg({
    id: 'termination.revoke.error.blockedByInvoices',
    message: 'Confirm the issued invoices the revoke cancels and regenerates',
  }),

  // Group
  GROUP_NOT_FOUND: msg`Group not found`,

  // CRM prospect (issue #315)
  PROSPECT_NOT_FOUND: msg({
    id: 'prospects.error.notFound',
    message: 'Prospect not found',
  }),
  PROSPECT_VERSION_CONFLICT: msg({
    id: 'prospects.error.versionConflict',
    message: 'Someone else just changed this prospect - the latest version has been reloaded, please try again',
  }),
  PROSPECT_CONTACT_REQUIRED: msg({
    id: 'prospects.error.contactRequired',
    message: 'A phone number or an email address is required for Parent 1',
  }),
  PROSPECT_CHILDCARE_REQUIRED: msg({
    id: 'prospects.error.childcareRequired',
    message: 'Select at least one desired childcare location',
  }),
  PROSPECT_CHILDCARE_NOT_FOUND: msg({
    id: 'prospects.error.childcareNotFound',
    message: 'One of the selected childcare locations does not exist in your organisation',
  }),
  PROSPECT_INVALID_STATUS_TRANSITION: msg({
    id: 'prospects.error.invalidStatusTransition',
    message: 'This status change is not allowed - the pipeline advances one step at a time',
  }),
  PROSPECT_ARCHIVE_REASON_REQUIRED: msg({
    id: 'prospects.error.archiveReasonRequired',
    message: 'Enter a reason to archive this prospect',
  }),
  PROSPECT_ARCHIVE_REASON_CODE_REQUIRED: msg({
    id: 'prospects.error.archiveReasonCodeRequired',
    message: 'Select why this enquiry is being archived',
  }),
  PROSPECT_ARCHIVE_FIELDS_NOT_APPLICABLE: msg({
    id: 'prospects.error.archiveFieldsNotApplicable',
    message: 'An archive reason can only be given when archiving this prospect',
  }),
  PROSPECT_COMMUNICATION_NOT_FOUND: msg({
    id: 'prospects.error.communicationNotFound',
    message: 'Communication entry not found',
  }),

  // CRM prospect owner (issue #1482)
  PROSPECT_OWNER_INVALID: msg({
    id: 'prospects.error.ownerInvalid',
    message: 'This employee cannot be made responsible for a prospect',
  }),

  // CRM prospect conversion (issue #1488)
  PROSPECT_CONVERT_DUPLICATE_CHILD: msg({
    id: 'prospects.error.convertDuplicateChild',
    message: 'A child with this name and date of birth already exists in your organisation',
  }),
  PROSPECT_CONVERT_CHILD_INVALID: msg({
    id: 'prospects.error.convertChildInvalid',
    message: 'This child cannot be linked to this enquiry',
  }),

  // CRM prospect email templates (issue #318)
  PROSPECT_EMAIL_TEMPLATE_NOT_FOUND: msg({
    id: 'prospectEmail.error.templateNotFound',
    message: 'Email template not found',
  }),
  PROSPECT_TEMPLATE_INVALID_PLACEHOLDER: msg({
    id: 'prospectEmail.error.invalidPlaceholder',
    message: 'The template uses a placeholder that does not exist',
  }),
  PROSPECT_TEMPLATE_INVALID: msg({
    id: 'prospectEmail.error.templateInvalid',
    message: 'The template could not be read - check the placeholder braces',
  }),
  PROSPECT_TEMPLATE_RENDER_FAILED: msg({
    id: 'prospectEmail.error.renderFailed',
    message: 'This saved template could not be rendered - fix it or reset it to the default',
  }),

  // CRM website lead intake (issue #317)
  INTAKE_KEY_NOT_FOUND: msg({
    id: 'prospectIntake.error.keyNotFound',
    message: 'This intake key no longer exists',
  }),
  INTAKE_KEY_INVALID: msg({
    id: 'prospectIntake.error.keyInvalid',
    message: 'The intake key is invalid, revoked or belongs to another organisation',
  }),
  INTAKE_RATE_LIMITED: msg({
    id: 'prospectIntake.error.rateLimited',
    message: 'Too many intake requests - please try again shortly',
  }),
  INTAKE_DUPLICATE: msg({
    id: 'prospectIntake.error.duplicate',
    message: 'This enquiry was already received',
  }),
  INTAKE_CONTACT_REQUIRED: msg({
    id: 'prospectIntake.error.contactRequired',
    message: 'A phone number or an email address is required',
  }),
  INTAKE_CHILDCARE_NOT_FOUND: msg({
    id: 'prospectIntake.error.childcareNotFound',
    message: 'One of the selected childcare locations does not exist or is not active',
  }),

  // CRM prospect tour (issue #319)
  PROSPECT_TOUR_NOT_FOUND: msg({
    id: 'prospectTour.error.notFound',
    message: 'This tour no longer exists',
  }),
  PROSPECT_TOUR_ALREADY_SCHEDULED: msg({
    id: 'prospectTour.error.alreadyScheduled',
    message: 'A tour is already scheduled for this prospect - move or delete it first',
  }),
  PROSPECT_TOUR_STATUS_NOT_ALLOWED: msg({
    id: 'prospectTour.error.statusNotAllowed',
    message: 'A tour can only be scheduled while the prospect is new or in progress',
  }),
  PROSPECT_TOUR_COMPLETE_STATUS_NOT_ALLOWED: msg({
    id: 'prospectTour.error.completeStatusNotAllowed',
    message: "A tour can only be completed while the prospect is in 'tour scheduled'",
  }),
  PROSPECT_TOUR_CLOSE_STATUS_NOT_ALLOWED: msg({
    id: 'prospectTour.error.closeStatusNotAllowed',
    message:
      'A tour can only be cancelled or marked as a no-show while the prospect is in tour scheduled',
  }),
  PROSPECT_TOUR_NOT_SCHEDULED: msg({
    id: 'prospectTour.error.notScheduled',
    message: 'This tour is no longer scheduled - someone else just completed or deleted it',
  }),
  PROSPECT_TOUR_CHILDCARE_NOT_FOUND: msg({
    id: 'prospectTour.error.childcareNotFound',
    message: 'The selected tour location does not exist in your organisation',
  }),
  PROSPECT_TOUR_EMPLOYEE_NOT_FOUND: msg({
    id: 'prospectTour.error.employeeNotFound',
    message: 'The selected staff member does not exist in your organisation',
  }),
  CALENDAR_EVENT_PROSPECT_TOUR_IMMUTABLE: msg({
    id: 'prospectTour.error.calendarEventImmutable',
    message: 'This appointment belongs to a prospect tour - move or delete it in the prospect record',
  }),

  // Org hierarchy pointers (issue #1217)
  DIRECT_HEAD_NOT_FOUND: msg({
    id: 'employees.error.directHeadNotFound',
    message: 'The selected direct head does not exist in your organisation',
  }),
  DIRECT_HEAD_CYCLE: msg({
    id: 'employees.error.directHeadCycle',
    message:
      'The selected employee reports to this employee, so this would create a reporting cycle',
  }),
  GROUP_LEAD_NOT_FOUND: msg({
    id: 'groups.error.leadNotFound',
    message: 'The selected group lead does not exist in your organisation',
  }),

  // Shift
  SHIFT_NOT_FOUND: msg`Shift not found`,
  SHIFT_CONFLICT: msg`Shift conflicts with an existing shift`,
  EMPLOYEE_NOT_FOUND: msg`Employee not found`,
  OPTIMISTIC_LOCK: msg`Someone else just changed this — please reload and try again`,
  EMPLOYEE_ABSENT: msg`Employee is absent on this date`,
  PATTERN_NOT_FOUND: msg`Shift pattern not found`,
  // Open shifts, claims and swaps (#2040)
  OPEN_SHIFT_NOT_RECURRING: msg({ id: 'shifts.error.openNotRecurring', message: 'An open shift is a single shift. Set Recurrence to None.' }),
  OPEN_SHIFT_NEEDS_GROUP: msg({ id: 'shifts.error.openNeedsGroup', message: 'An open shift needs at least one group, so the employees of that group can claim it.' }),
  OPEN_SHIFT_NOT_CONFIRMABLE: msg({ id: 'shifts.error.openNotConfirmable', message: 'An open shift cannot be confirmed: nobody is assigned yet.' }),
  OPEN_SHIFT_EMPLOYEE_MISMATCH: msg({ id: 'shifts.error.openEmployeeMismatch', message: 'Choose an employee or Open shift.' }),
  SHIFT_NOT_OPEN: msg({ id: 'shiftRequests.error.notOpen', message: 'This shift is no longer open.' }),
  SHIFT_REQUEST_EXISTS: msg({ id: 'shiftRequests.error.exists', message: 'There is already an open request for this shift.' }),
  SWAP_CUTOFF: msg({ id: 'shiftRequests.error.swapCutoff', message: 'Swaps are possible until 24 hours before the shift starts.' }),
  SHIFT_NOT_SWAPPABLE: msg({ id: 'shiftRequests.error.notSwappable', message: 'Only a published or confirmed shift can be offered for a swap.' }),
  SWAP_COLLEAGUE_INVALID: msg({ id: 'shiftRequests.error.colleagueInvalid', message: 'This colleague cannot take the shift.' }),
  SHIFT_REQUEST_NOT_FOUND: msg({ id: 'shiftRequests.error.notFound', message: 'This request no longer exists.' }),
  SHIFT_REQUEST_CLOSED: msg({ id: 'shiftRequests.error.closed', message: 'This request was already decided, withdrawn or replaced.' }),
  SHIFT_REQUEST_NOT_DECIDABLE: msg({ id: 'shiftRequests.error.notDecidable', message: 'No colleague has accepted the swap yet, so there is nothing to decide.' }),
  SHIFT_REQUEST_STALE: msg({ id: 'shiftRequests.error.stale', message: 'The shift changed or was already given to someone else. Reload the planner.' }),
  SHIFT_REQUEST_NOT_DECIDER: msg({ id: 'shiftRequests.error.notDecider', message: 'Only the group lead of this shift or an admin decides this request.' }),
  SHIFT_REQUEST_SELF_DECISION: msg({ id: 'shiftRequests.error.selfDecision', message: 'You cannot decide a request that gives the shift to you.' }),
  SHIFT_REQUEST_SYSTEM_ACTION: msg({ id: 'shiftRequests.error.systemAction', message: 'Use Approve or Reject for this request.' }),
  SHIFT_WRITE_OUT_OF_SCOPE: msg`You can only plan shifts of the groups you lead`,

  // Invoice
  INVOICE_NOT_FOUND: msg`Invoice not found`,
  INVOICE_INVALID_TRANSITION: msg`Invalid status transition`,
  INVOICE_NON_DRAFT: msg`Cannot modify a non-draft invoice`,
  INVOICE_CONTRACT_NO_SERVICES: msg`Contract has no services defined`,
  // #1697/#1703: regenerate on a contract group whose settling-in plan is still open
  // (or whose period ends before the first billing day).
  SETTLING_IN_NOT_CLOSED: msg({
    id: 'invoices.error.settlingInNotClosed',
    message:
      'The settling-in is not yet completed and signed off. Until then, no invoices are created for this contract.',
  }),
  // #1697: regenerate on a later month while the month of the first billing day F
  // has no invoice yet.
  SETTLING_IN_FIRST_PERIOD_MISSING: msg({
    id: 'invoices.error.settlingInFirstPeriodMissing',
    message:
      'Generate the invoice for the first billing month after the settling-in first. It carries the settling-in fee.',
  }),

  // Activity
  ACTIVITY_NOT_FOUND: msg`Activity not found`,
  ACTIVITY_CATEGORY_IMMUTABLE: msg`Activity category cannot be changed`,
  IDEMPOTENCY_CONFLICT: msg`A conflicting request is already in progress`,

  // Messaging
  CONVERSATION_NOT_FOUND: msg`Conversation not found`,
  MESSAGE_NOT_FOUND: msg`Message not found`,
  PERMISSION_DENIED: msg`You don't have permission to do this`,
  EDIT_WINDOW_EXPIRED: msg`The edit window has expired`,
  TEMPLATE_NOT_FOUND: msg`Template not found`,
  NOT_MESSAGE_OWNER: msg`You can only edit or delete your own messages`,
  CHILD_NOT_IN_GROUPS: msg`Child not found or not in the allowed groups`,
  CONTACT_NOT_PARENT: msg`This contact is not a parent or guardian of the child`,
  CONTACT_NO_APP_ACCESS: msg`This contact does not have app access`,
  TOO_MANY_PARTICIPANTS: msg`A conversation can have at most 2 participants`,
  INVALID_ATTACHMENT_CONTENT_TYPE: msg`This file type is not allowed`,
  FILE_TOO_LARGE: msg`File exceeds the maximum allowed size`,

  // Course
  COURSE_NOT_FOUND: msg`Course not found`,
  COURSE_NAME_EXISTS: msg`A course with this name already exists`,
  COURSE_VERSION_CONFLICT: msg`The course was changed by someone else — please reload`,
  COURSE_ALREADY_ARCHIVED: msg`This course is already archived`,
  COURSE_NOT_ARCHIVED: msg`This course is not archived`,
  COURSE_PLAN_NOT_FOUND: msg`Course plan not found`,
  COURSE_PLAN_VERSION_CONFLICT: msg`The course plan was changed by someone else — please reload`,
  COURSE_PLAN_ALREADY_ARCHIVED: msg`This course plan is already archived`,
  COURSE_PLAN_NOT_ARCHIVED: msg`This course plan is not archived`,
  ENROLLMENT_NOT_FOUND: msg`Enrollment not found`,
  ENROLLMENT_DUPLICATE: msg`Already enrolled in this course`,
  ENROLLMENT_CAPACITY_FULL: msg`Course is at full capacity`,
  ENROLLMENT_ALREADY_CANCELLED: msg`Enrollment already cancelled`,
  ATTENDANCE_NOT_FOUND: msg`Attendance not found`,
  ATTENDANCE_DUPLICATE: msg`Attendance already recorded for this date`,
  ATTENDANCE_ALREADY_BILLED: msg`Attendance already billed and cannot be modified`,

  // Calendar
  EVENT_NOT_FOUND: msg`Event not found`,

  // Child / Contact
  CHILD_NOT_FOUND: msg`Child not found`,
  CONTACT_NOT_FOUND: msg`Contact not found`,

  // Child signed-contract document (issue #1248)
  CONTRACT_DOCUMENT_NOT_FOUND: msg({
    id: 'children.error.contractDocumentNotFound',
    message: 'No signed contract is attached to this child',
  }),
  CONTRACT_DOCUMENT_INVALID: msg({
    id: 'children.error.contractDocumentInvalid',
    message: 'The selected file cannot be attached as the signed contract',
  }),

  // Child profile photo (staff-managed, employee app)
  CHILD_AVATAR_INVALID: msg({
    id: 'children.error.childAvatarInvalid',
    message: 'The selected image cannot be used as the profile photo',
  }),
  CHILD_AVATAR_NOT_FOUND: msg({
    id: 'children.error.childAvatarNotFound',
    message: 'Profile photo not found',
  }),

  // Absence
  ABSENCE_NOT_FOUND: msg`Absence not found`,
  ABSENCE_OVERLAP: msg`Absence overlaps with an existing absence`,
  ABSENCE_OPTIMISTIC_LOCK: msg`The absence was changed by someone else — please reload`,
  ABSENCE_TYPE_NOT_REQUESTABLE: msg({
    id: 'absences.error.typeNotRequestable',
    message: 'This absence type cannot be requested.',
  }),
  ABSENCE_NOT_CANCELLABLE: msg({
    id: 'absences.error.notCancellable',
    message: 'This request can no longer be withdrawn.',
  }),
  INVALID_ACCIDENT_DATE: msg({
    id: 'absences.error.invalidAccidentDate',
    message: 'The accident date must be on or before the first day of absence and not in the future.',
  }),
  ABSENCE_CERTIFICATE_NOT_FOUND: msg({
    id: 'absences.error.certificateNotFound',
    message: 'Certificate not found.',
  }),

  // Absence reports and certificates (#1767)
  ABSENCE_REPORT_TOO_OLD: msg({
    id: 'absences.error.reportTooOld',
    message: 'The absence starts too long ago to be reported.',
  }),
  ABSENCE_REPORT_NOT_ACTIVE: msg({
    id: 'absences.error.reportNotActive',
    message: 'This report is no longer active.',
  }),
  ABSENCE_REPORT_CONFLICT: msg({
    id: 'absences.error.reportConflict',
    message: 'This report was changed in the meantime. Reload and try again.',
  }),
  ABSENCE_NOT_SELF_REPORT: msg({
    id: 'absences.error.notSelfReport',
    message: 'This absence is not a report.',
  }),
  ABSENCE_SELF_DECISION: msg({
    id: 'absences.error.selfDecision',
    message: 'You cannot act on your own absence.',
  }),
  ACCIDENT_FIELDS_REQUIRED: msg({
    id: 'absences.error.accidentFieldsRequired',
    message:
      'An accident report needs the accident date and whether it was work related. Only the employee can report an accident.',
  }),
  CERTIFICATE_MODE_NOT_EAU: msg({
    id: 'absences.error.certificateModeNotEau',
    message: 'The organisation does not use electronic certificates.',
  }),
  CERTIFICATE_LOCKED: msg({
    id: 'absences.error.certificateLocked',
    message: 'The certificate can no longer be deleted.',
  }),
  CERTIFICATE_IMAGE_UNPROCESSABLE: msg({
    id: 'absences.error.certificateImageUnprocessable',
    message: 'The image could not be processed. Upload a different file.',
  }),
  CERTIFICATE_STORAGE_UNAVAILABLE: msg({
    id: 'absences.error.certificateStorageUnavailable',
    message: 'Certificate storage is not available. Try again later.',
  }),

  // Extra daycare (#1649). Half-open: adjacent windows are accepted, so this
  // only ever means a real double booking of the same hours.
  EXTRA_DAYCARE_OVERLAP: msg({
    id: 'extraDaycare.error.overlap',
    message: 'This child already has an extra day covering that time',
  }),

  // Organisation time tracking switch (#1903)
  TIME_TRACKING_DISABLED: msg({
    id: 'backendError.timeTrackingDisabled',
    message: 'Time tracking is turned off for your organisation.',
  }),

  // Organisation group presence switch (#2113)
  GROUP_PRESENCE_DISABLED: msg({
    id: 'backendError.groupPresenceDisabled',
    message: 'Group presence is turned off for your organisation.',
  }),

  // Change request (care_day / pause create — #783/#784). All the care_day and
  // pause validation sentinels (invalid weekday, invalid window, the 5-day
  // attendance cap) map to this single 400-class code server-side, so surface one
  // clear, actionable message instead of a raw error.
  CHANGE_REQUEST_UNSUPPORTED_SUBTYPE: msg`This change could not be applied. Check the weekday, the date window, and the 5-day attendance cap.`,

  // Workflow
  WORKFLOW_ITEM_NOT_FOUND: msg`Workflow item not found`,
  WORKFLOW_INVALID_ACTION: msg`Invalid workflow action`,
  WORKFLOW_ITEM_COMPLETED: msg`Workflow item is already completed`,
  WORKFLOW_OPTIMISTIC_LOCK: msg`The workflow item was changed by someone else — please reload`,

  // Employee role
  EMPLOYEE_ROLE_DUPLICATE: msg`Employee already has this role assigned`,

  // Settling-in
  SETTLING_IN_PLAN_NOT_FOUND: msg`Settling-in plan not found`,
  SETTLING_IN_PLAN_OVERLAP: msg`An active settling-in plan already exists for this child`,
  SETTLING_IN_BILLING_NOT_CONFIGURED: msg`Settling-in billing is not configured`,
  SETTLING_IN_PLAN_COMPLETED: msg`Settling-in plan is already completed`,
  SETTLING_IN_VERSION_CONFLICT: msg`The settling-in record was changed by someone else — please reload`,
  SCHEDULE_ENTRY_NOT_FOUND: msg`Schedule entry not found`,
  TRACKING_ENTRY_NOT_FOUND: msg`Tracking entry not found`,
  PROGRESS_ENTRY_NOT_FOUND: msg`Progress entry not found`,
  SCHEDULE_ENTRY_DUPLICATE_DATE: msg`Schedule entry already exists for this date`,
  TRACKING_ENTRY_DUPLICATE_DATE: msg`Tracking entry already exists for this date`,
  PROGRESS_ENTRY_DUPLICATE_DATE: msg`Progress entry already exists for this date`,
  INVALID_INDICATOR_VALUE: msg`Invalid indicator value`,
  INVALID_TIME_RANGE: msg`"From" time must be before "to" time`,

  // Weekly activity
  WEEKLY_ACTIVITY_NOT_FOUND: msg`Weekly activity not found`,
  WEEKLY_ACTIVITY_VERSION_CONFLICT: msg`The weekly activity was changed by someone else — please reload`,
  INVALID_GROUP_ID: msg`One or more selected groups are invalid`,
  INVALID_CHILDCARE_ID: msg`One or more selected childcares are invalid`,

  // Generic
  CONCURRENT_MODIFICATION: msg`This record was changed by someone else — please reload and try again`,
  ENTITY_STATUS_DERIVED: msg`The status is now derived from this record's contracts and can no longer be set by hand. Your other changes were not saved — please review and save again.`,

  // Reconciliation — per-application Undo (issue 484)
  fifo_drift: msg`Can't undo: this payment's credit has already been put toward an invoice. Undo that first, then try again.`,
  APPLICATION_NOT_MANUAL: msg`Only payments you confirmed by hand can be undone here.`,
  REFUND_EXCEEDS_CREDIT: msg`This refund is bigger than the account's available credit. Refunds can only pay back money the account is actually holding — if you are charging the account instead, post a correction.`,

  // Subsidy — slice E (subsidy debtor feature)
  SUBSIDY_OFFICE_DEBTOR_NOT_CONFIGURED: msg`This subsidy office has no account set up yet. Add one in the office's settings, then regenerate the invoice.`,
  NO_SUBSIDY_RATE_CONFIG: msg`No subsidy rate version covers this invoice's period. The parent will be billed the full Tarif until rates are configured for this period.`,
  SUBSIDY_OFFICE_DEBTOR_MISSING: msg`This subsidy office has no account set up, so only the family's share was billed.`,
  SUBSIDY_ENGINE_CLAMPED_TO_ZERO: msg`The calculated parent contribution exceeded the tariff; the subsidy was clamped to zero. Check the subsidy rate configuration for this period.`,

  // Annual confirmation (#1862)
  NO_INVOICES_FOR_YEAR: msg`There are no invoices for this year.`,
  NOT_A_PARENT_DEBTOR: msg`An annual confirmation is only available for parent accounts.`,
  // Annual confirmation finalization (#1975)
  ANNUAL_CONFIRMATION_NOT_FINALIZED: msg({
    id: "backendError.annualConfirmation.notFinalized",
    message: "The daycare has not yet closed this year.",
  }),
  ANNUAL_CONFIRMATION_CHILD_NOT_ON_ACCOUNT: msg({
    id: "backendError.annualConfirmation.childNotOnAccount",
    message: "This child has no annual confirmation on this account in this year.",
  }),
  ANNUAL_CONFIRMATION_NOT_FINAL: msg({
    id: "backendError.annualConfirmation.notFinal",
    message: "This year is not finalized for this child.",
  }),

  // Reconciliation — subsidy office report import ("Abrechnung gemaess Auszahlung")
  SUBSIDY_REPORT_DUPLICATE: msg`This file was already uploaded. Upload the corrected file if the office sent a new one.`,
  SUBSIDY_REPORT_UNREADABLE: msg`This file could not be read. Open it in Excel and save it again as .xls or .xlsx, then retry.`,
  SUBSIDY_REPORT_MISSING_COLUMN: msg`The sheet is missing a column the import needs. Upload the office's original export, not an edited copy.`,
  SUBSIDY_REPORT_BAD_CELL: msg`The sheet contains a value that could not be read as a number or a date.`,
  SUBSIDY_REPORT_TOO_MANY_LINES: msg`This sheet has too many rows to import.`,
  SUBSIDY_REPORT_UNSUPPORTED_FORMAT: msg`Only .xls and .xlsx files are accepted.`,
  SUBSIDY_REPORT_PAYMENT_NOT_ON_OFFICE: msg`That payment belongs to a different counterparty, so it can't be linked to this office's report.`,
  SUBSIDY_REPORT_SUPERSEDED: msg`This report was replaced by a newer upload. Work on the newest one.`,
  SUBSIDY_OFFICE_CHILD_REF_TAKEN: msg({
    id: 'reconciliation.error.subsidyOfficeChildRefTaken',
    message: 'This PID is already entered for another child. Correct it there first.',
  }),
  SUBSIDY_OFFICE_CHILD_REF_INVALID: msg({
    id: 'reconciliation.error.subsidyOfficeChildRefInvalid',
    message: 'Enter the PID with 1 to 50 characters.',
  }),
  SUBSIDY_REPORT_NOT_FOUND: msg`Report not found`,
  SUBSIDY_REPORT_LINE_NOT_FOUND: msg`Report line not found`,
  SUBSIDY_REPORT_NOT_LINKED: msg({
    id: 'reconciliation.error.subsidyReportNotLinked',
    message: 'This statement is not linked to a payment yet. Link it first, then adopt the office amounts.',
  }),
  SUBSIDY_ROUNDING_ADOPTION_DISABLED: msg({
    id: 'reconciliation.error.subsidyRoundingAdoptionDisabled',
    message: "Adopting the office's rounded amounts is turned off. Turn it on in the billing settings first.",
  }),

  // Billing settings — BESR-ID change after a delivered invoice email (issue #1756)
  QRR_SCHEME_LOCKED: msg({
    id: 'billingSettings.besrId.locked',
    message: 'The BESR-ID cannot be set or changed: invoices of this organisation were already sent by email with the current QR references.',
  }),

  // Reconciliation — office reclaim netting (issue #1576)
  RECLAIM_NETTING_OUT_OF_RANGE: msg({
    id: 'reconciliation.error.reclaimNetting.outOfRange',
    message: "The amount must be above zero and no larger than the open reclaim or the earlier payment's open amount.",
  }),
  RECLAIM_NETTING_NOT_OFFICE_PAYMENT: msg({
    id: 'reconciliation.error.reclaimNetting.notOfficePayment',
    message: 'Both payments must be subsidy office payments.',
  }),
  RECLAIM_NETTING_DEBTOR_MISMATCH: msg({
    id: 'reconciliation.error.reclaimNetting.debtorMismatch',
    message: 'Both payments must come from the same subsidy office.',
  }),
  RECLAIM_NETTING_SAME_PAYMENT: msg({
    id: 'reconciliation.error.reclaimNetting.samePayment',
    message: 'A payment cannot be netted with itself. Pick another office payment as the source.',
  }),
  RECLAIM_NETTING_REPORT_LINES_INVALID: msg({
    id: 'reconciliation.error.reclaimNetting.reportLinesInvalid',
    message: 'The statement lines no longer match this child-month. Reload the statement and try again.',
  }),
  SUBSIDY_STATEMENT_UNRESOLVED_LINES: msg({
    id: 'reconciliation.error.statementUnresolvedLines',
    message: 'Some lines of this statement are not matched to a child yet. Match them first, then try again.',
  }),
  RECLAIM_NETTING_CONSUMED: msg({
    id: 'reconciliation.error.reclaimNetting.consumed',
    message: 'The netted money is already assigned on the later payment. Undo the allocations from the target payment first, then undo the netting.',
  }),
  RECLAIM_NETTING_PAYMENT_LOCKED: msg({
    id: 'reconciliation.error.reclaimNetting.paymentLocked',
    message: 'One of the two payments is locked and can no longer be changed.',
  }),
  DUPLICATE_REVERT_NOT_IMPORT: msg({
    id: 'reconciliation.error.duplicateRevertNotImport',
    message: 'Only bank transactions from a statement import can be removed as duplicates.',
  }),
  DUPLICATE_REVERT_OFFICE_PAYMENT: msg({
    id: 'reconciliation.error.duplicateRevertOfficePayment',
    message: 'This payment belongs to a subsidy office. Undo it from the subsidy matching tab.',
  }),
  DUPLICATE_REVERT_HAS_REFUND: msg({
    id: 'reconciliation.error.duplicateRevertHasRefund',
    message: 'A refund or settlement write-off was booked against this payment. Undo that first.',
  }),
  RECLAIM_NETTING_ALREADY_REVERTED: msg({
    id: 'reconciliation.error.reclaimNetting.alreadyReverted',
    message: 'This netting was already undone. Reload to see the current state.',
  }),

  // Invoice overrides / flexible corrections (issues 708/709)
  PARENT_OVERRIDE_NOT_SUPPORTED: msg`Parent-scope corrections are not yet available`,
  INVALID_OVERRIDE_KIND: msg`Invalid correction type`,
  INVALID_OVERRIDE_SCOPE: msg`Invalid correction scope`,
  INVOICE_OVERRIDE_NOT_FOUND: msg`Correction not found`,
  CANNOT_OVERRIDE_CLOSED_INVOICE: msg`Cannot correct a cancelled or credited invoice`,
  NO_PARENT_SPLIT_FOR_OVERRIDE: msg`This invoice has no family share, so a family correction doesn't apply`,
  SUBSIDY_CORRECTION_BELOW_PAID: msg`Cannot lower the subvention below the amount already paid by the office. Release the office payment first, then correct.`,
  OVERRIDE_PIN_ALREADY_EXISTS: msg`Only one correction of this type is allowed per invoice. Edit or remove the existing correction first.`,
  INVOICE_NOT_RESENDABLE: msg`This invoice can't be resent in its current status`,

  // Employee login provisioning (issue #354)
  CLERK_NOT_CONFIGURED: msg`Login creation isn't set up on this server yet. Contact support.`,
  EMPLOYEE_LOGIN_ALREADY_LINKED: msg`This employee already has a login.`,
  EMPLOYEE_LOGIN_NO_EMAIL: msg`Add a valid email address to this employee before creating a login.`,
  EMPLOYEE_LOGIN_CLERK_USER_EXISTS: msg`A login already exists for this email address. Link it to this employee instead.`,
  EMPLOYEE_LOGIN_CLERK_USER_TAKEN: msg`That login is already linked to another employee in your organisation.`,
  EMPLOYEE_LOGIN_ORG_MEMBER_LIMIT: msg`Your organisation has reached its login limit. Contact support to add more.`,
  EMPLOYEE_LOGIN_NOT_LINKED: msg`This employee has no login to re-issue yet.`,
  EMPLOYEE_LOGIN_PROVISION_FAILED: msg`Couldn't create the login. Please try again.`,
  // Revoke login (issue #1255).
  EMPLOYEE_LOGIN_CANNOT_REVOKE_SELF: msg`You cannot revoke your own login.`,
  EMPLOYEE_LOGIN_CONCURRENT_MODIFICATION: msg`This login was changed by someone else. Reload the employee and try again.`,
  // Shared-account guard (issue #1892 security). Explicit dotted id.
  EMPLOYEE_LOGIN_SHARED_ACCOUNT: msg({
    id: 'employees.login.errorCode.sharedAccount',
    message:
      'This login is also used outside this staff login. The employee must reset the password with "Forgot password" on the sign-in screen.',
  }),
  // Soft-delete guard (issue #1303). Explicit dotted id, like the capacity
  // entries below: this is user-visible copy, so a re-wording must not churn
  // the msgid in all 6 catalogs.
  EMPLOYEE_HAS_ACTIVE_LOGIN: msg({
    id: 'employees.errors.hasActiveLogin',
    message: "Revoke this employee's login before deleting them.",
  }),

  // Parent login provisioning (issue #1892). Explicit dotted ids.
  CONTACT_LOGIN_EMAIL_REQUIRED: msg({
    id: 'contacts.login.errorCode.emailRequired',
    message: 'Add a valid main email address to this contact before creating a login.',
  }),
  CONTACT_LOGIN_NOT_A_PARENT: msg({
    id: 'contacts.login.errorCode.notAParent',
    message: 'This contact is not recorded as a parent of any child. Only parents get a parent app login.',
  }),
  CONTACT_LOGIN_CLERK_USER_EXISTS: msg({
    id: 'contacts.login.errorCode.clerkUserExists',
    message: 'A login already exists for this email address. Link it to this parent instead.',
  }),
  CONTACT_LOGIN_CLERK_USER_TAKEN: msg({
    id: 'contacts.login.errorCode.clerkUserTaken',
    message: 'That login is already linked to another contact in your organisation.',
  }),
  CONTACT_LOGIN_ALREADY_LINKED: msg({
    id: 'contacts.login.errorCode.alreadyLinked',
    message: 'This contact already has a login.',
  }),
  CONTACT_LOGIN_NOT_LINKED: msg({
    id: 'contacts.login.errorCode.notLinked',
    message: 'This contact has no login yet.',
  }),
  CONTACT_LOGIN_CONCURRENT_MODIFICATION: msg({
    id: 'contacts.login.errorCode.concurrentModification',
    message: 'This login was changed by someone else. Reload the contact and try again.',
  }),
  CONTACT_LOGIN_ORG_MEMBER_LIMIT: msg({
    id: 'contacts.login.errorCode.orgMemberLimit',
    message: 'Your organisation has reached its login limit. Contact support to add more.',
  }),
  CONTACT_LOGIN_PROVISION_FAILED: msg({
    id: 'contacts.login.errorCode.provisionFailed',
    message: 'Could not create the login. Please try again.',
  }),
  CONTACT_LOGIN_CANNOT_REVOKE_SELF: msg({
    id: 'contacts.login.errorCode.cannotRevokeSelf',
    message: 'You cannot revoke your own login.',
  }),
  CONTACT_HAS_ACTIVE_LOGIN: msg({
    id: 'contacts.login.errorCode.hasActiveLogin',
    message: "Revoke this contact's login before deleting them.",
  }),
  CONTACT_LOGIN_SHARED_ACCOUNT: msg({
    id: 'contacts.login.errorCode.sharedAccount',
    message:
      'This login is also used outside this parent login. The parent must reset the password with "Forgot password" on the sign-in screen.',
  }),

  // Capacity bulk move (CAP-4c / C17–C19). Breaches are warn-and-allow; only
  // SAME_WINDOW_DOUBLE_PLACEMENT is a true hard block.
  // Explicit dotted ids: these are user-visible copy, so the msgid must not be
  // the English string itself — a copy edit would otherwise churn all 6
  // catalogs.
  // NOTE the `group` prefix: BulkMoveWizardStepReview.tsx already owns
  // capacity.move.breach.{warning,critical} for its per-WINDOW copy ("This
  // window will be over capacity"). These two are the whole-GROUP wording the
  // backend breach codes map to, so they need distinct ids — reusing the
  // window ids makes `lingui extract` fail with "Encountered different default
  // translations for message ...".
  MOVE_BREACH_WARNING: msg({
    id: 'capacity.move.breach.groupWarning',
    message: 'This group will be near capacity after the move',
  }),
  MOVE_BREACH_CRITICAL: msg({
    id: 'capacity.move.breach.groupCritical',
    message: 'This group will be over capacity after the move',
  }),
  SAME_WINDOW_DOUBLE_PLACEMENT: msg({
    id: 'capacity.move.blocked.sameWindowDoublePlacement',
    message:
      'This child would be in two groups during the same service window — resolve the other allocation first',
  }),
  NO_CURRENT_ALLOCATION: msg({
    id: 'capacity.move.blocked.noCurrentAllocation',
    message: 'This child has no allocation covering the effective date',
  }),

  // Subsidy amend (deliverable C step 2 — apply a subsidy office/share change to
  // already-billed invoices). Each of these is an instruction: the operator has
  // to do something else first, so retrying the same action cannot work.
  SUBSIDY_AMEND_NO_OFFICE: msg({
    id: 'subsidyAmend.error.noOffice',
    message:
      "This contract has no city/office subsidy set, so there's nothing to apply to the billed invoices.",
  }),
  SUBSIDY_AMEND_OFFICE_NOT_FOUND: msg({
    id: 'subsidyAmend.error.officeNotFound',
    message: 'The subsidy office could not be found.',
  }),
  SUBSIDY_AMEND_INVOICE_NOT_BILLED: msg({
    id: 'subsidyAmend.error.invoiceNotBilled',
    message: "One of the selected invoices isn't a billed invoice on this contract.",
  }),
  SUBSIDY_AMEND_NO_SUBSIDY_LINE: msg({
    id: 'subsidyAmend.error.noSubsidyLine',
    message: 'That invoice has no office share to update.',
  }),
  SUBSIDY_AMEND_PAYMENT_SPANS_INVOICES: msg({
    id: 'subsidyAmend.error.paymentSpansInvoices',
    message:
      "One of this invoice's office payments also covers other invoices. Move that payment from the office's account first, then apply.",
  }),
  SUBSIDY_AMEND_AMOUNT_BELOW_PAID: msg({
    id: 'subsidyAmend.error.amountBelowPaid',
    message:
      "The new office share is lower than what the office has already paid on this invoice. Return that payment to the office's account first, then lower the amount.",
  }),
  SUBSIDY_AMEND_INVALID_INPUT: msg({
    id: 'subsidyAmend.error.invalidInput',
    message: 'The request was incomplete. Pick at least one invoice and a valid amount.',
  }),
  ALREADY_IN_TARGET_GROUP: msg({
    id: 'capacity.move.blocked.alreadyInTargetGroup',
    message: 'This child is already allocated to the target group on the effective date',
  }),

  // Child media & documentation consent (Epic AB, issue #1076).
  // NOTE: a STALE consent token comes back as the shared `OPTIMISTIC_LOCK`
  // above — there is deliberately no CONSENT_OPTIMISTIC_LOCK code, and keying
  // conflict handling off one would silently miss every stale-token 409.
  CONSENT_NOT_FOUND: msg({
    id: 'children.consents.error.notFound',
    message: 'Consent record not found',
  }),
  CONSENT_INVALID_INPUT: msg({
    id: 'children.consents.error.invalidInput',
    message: 'Please check the consent details and try again',
  }),
  CONSENT_INVALID_REFERENCE: msg({
    id: 'children.consents.error.invalidReference',
    message: 'The selected contact or document is not available for this organisation',
  }),
  CONSENT_ALREADY_REVOKED: msg({
    id: 'children.consents.error.alreadyRevoked',
    message: 'This consent has already been revoked — please reload',
  }),
  // 503, not a 4xx (issue #1102): the write could not acquire the child lock
  // because a portfolio capture held it. Nothing was written and nothing is
  // wrong with the request, so the sentence says "try again", never "reload" —
  // an admin who reloads here would find the record exactly as they left it.
  CONSENT_LOCK_TIMEOUT: msg({
    id: 'children.consents.error.lockTimeout',
    message: 'The consent record could not be saved right now — please try again in a moment',
  }),

  // Portfolio & Bildungsdokumentation (Epic AB, issues #1075 / #1078).
  //
  // NOTE: a stale portfolio token comes back as the shared `OPTIMISTIC_LOCK`
  // above, which `internal/handlers/shared/error_helper.go` maps
  // `ErrPortfolioOptimisticLock` onto. `PORTFOLIO_OPTIMISTIC_LOCK` is mapped
  // here as well so a future split of that code still renders a sentence rather
  // than the generic fallback — `isPortfolioOptimisticLock` accepts both.
  PORTFOLIO_OPTIMISTIC_LOCK: msg({
    id: 'portfolio.error.optimisticLock',
    message: 'Someone else just changed this — the current version has been loaded',
  }),
  PORTFOLIO_ENTRY_NOT_FOUND: msg({
    id: 'portfolio.error.entryNotFound',
    message: 'This observation is no longer available',
  }),
  PORTFOLIO_BODY_REQUIRED: msg({
    id: 'portfolio.error.bodyRequired',
    message: 'Add an observation text before documenting this entry',
  }),
  PORTFOLIO_DOMAIN_REQUIRED: msg({
    id: 'portfolio.error.domainRequired',
    message: 'Tag at least one development area before documenting this entry',
  }),
  PORTFOLIO_ALREADY_DOCUMENTED: msg({
    id: 'portfolio.error.alreadyDocumented',
    message: 'This observation has already been documented',
  }),
  PORTFOLIO_NOT_DOCUMENTED: msg({
    id: 'portfolio.error.notDocumented',
    message: 'Only a documented observation can be shared with the parents',
  }),
  PORTFOLIO_ALREADY_RELEASED: msg({
    id: 'portfolio.error.alreadyReleased',
    message: 'This observation has already been shared with the parents',
  }),
  PORTFOLIO_NOT_RELEASED: msg({
    id: 'portfolio.error.notReleased',
    message: 'This observation is not shared with the parents',
  }),
  PORTFOLIO_INVALID_REFERENCE: msg({
    id: 'portfolio.error.invalidReference',
    message: 'A referenced entry is not available for this organisation',
  }),
  PORTFOLIO_INVALID_INPUT: msg({
    id: 'portfolio.error.invalidInput',
    message: 'Please check the values and try again',
  }),
  PORTFOLIO_CALLER_NOT_EMPLOYEE: msg({
    id: 'portfolio.error.callerNotEmployee',
    message: 'Your user account is not linked to an employee record',
  }),
  PORTFOLIO_SYSTEM_ROW_IMMUTABLE: msg({
    id: 'portfolio.error.systemRowImmutable',
    message:
      'System defaults are fixed: you can deactivate them, but not rename or delete them',
  }),
  PORTFOLIO_CONFIG_NOT_FOUND: msg({
    id: 'portfolio.error.configNotFound',
    message: 'This configuration entry is no longer available',
  }),
  PORTFOLIO_CONFIG_IN_USE: msg({
    id: 'portfolio.error.configInUse',
    message:
      'This entry is still used by existing observations — deactivate it instead of deleting it',
  }),

  // Entwicklungsraster (Epic AB, v2-1b — #1179 backend / #1181 admin web).
  //
  // PORTFOLIO_FRAMEWORK_DISABLED is mapped here for completeness, but the raster
  // SURFACES never render it: a disabled framework is the organisation's opt-out
  // and renders as absence, not as an error (`isFrameworkDisabledError`). The
  // sentence exists for the settings screens, where the same code can surface on
  // a write.
  PORTFOLIO_FRAMEWORK_DISABLED: msg({
    id: 'portfolio.error.frameworkDisabled',
    message: 'No development framework is enabled for your organisation',
  }),
  PORTFOLIO_FRAMEWORK_NOT_FOUND: msg({
    id: 'portfolio.error.frameworkNotFound',
    message: 'This development framework is no longer available',
  }),
  PORTFOLIO_FRAMEWORK_ITEM_NOT_FOUND: msg({
    id: 'portfolio.error.frameworkItemNotFound',
    message: 'This milestone is no longer available',
  }),
  PORTFOLIO_FRAMEWORK_BAND_NOT_ALLOWED: msg({
    id: 'portfolio.error.frameworkBandNotAllowed',
    message:
      'You can adapt the milestones of a framework, but not its age ranges — pick an age range the framework already has',
  }),
  PORTFOLIO_FRAMEWORK_ITEM_DOMAIN_IMMUTABLE: msg({
    id: 'portfolio.error.frameworkItemDomainImmutable',
    message: 'A milestone cannot be moved to a different development area',
  }),

  // Portfolio-Buch + Übergabe (Epic AB, v2-2 — #1193 backend / #1194 admin web).
  //
  // Only the four codes that can reach `backendErrorText` are mapped. The other
  // handover codes — the 409 conflict, the lost completion claim, the two
  // external-sharing gates and every export code — are branched on BEFORE the
  // generic renderer and have their own stated conditions on the surface, so a
  // sentence here would never be read. Unmapped, these four would render the
  // server's English message inside a German UI.
  PORTFOLIO_HANDOVER_NOT_FOUND: msg({
    id: 'portfolio.error.handoverNotFound',
    message: 'This handover record is no longer available',
  }),
  PORTFOLIO_HANDOVER_CHILD_NOT_FOUND: msg({
    id: 'portfolio.error.handoverChildNotFound',
    message: 'This child is no longer available in your organisation',
  }),
  PORTFOLIO_HANDOVER_GROUP_NOT_FOUND: msg({
    id: 'portfolio.error.handoverGroupNotFound',
    message: 'One of the selected groups is no longer available in your organisation',
  }),
  PORTFOLIO_HANDOVER_CONSENT_NOT_FOUND: msg({
    id: 'portfolio.error.handoverConsentNotFound',
    message: 'The recorded consent is no longer available. Reload the page and try again',
  }),

  // Access roles (issue #1669 backend / #1670 Settings -> Access roles page).
  ACCESS_ROLE_NOT_FOUND: msg({
    id: 'accessRoles.error.notFound',
    message: 'This role no longer exists',
  }),
  ACCESS_ROLE_UNKNOWN_MODULE: msg({
    id: 'accessRoles.error.unknownModule',
    message: 'One of the selected modules is unknown. Reload the page and try again.',
  }),
  ACCESS_ROLE_IN_USE: msg({
    id: 'accessRoles.error.inUse',
    message: 'This role is assigned to employees. Change their contracts first.',
  }),
  ACCESS_ROLE_INVALID_COPY_SOURCE: msg({
    id: 'accessRoles.error.invalidCopySource',
    message: 'The role to copy from no longer exists',
  }),
  ACCESS_ROLE_LABEL_EMPTY: msg({
    id: 'accessRoles.error.labelEmpty',
    message: 'Enter a name for the role',
  }),
  ACCESS_ROLE_NO_FREE_VALUE: msg({
    id: 'accessRoles.error.noFreeValue',
    message: 'Too many roles have this name. Choose another name.',
  }),

  // Kita devices (issue #1766, Settings -> Devices and the employee app).
  // ENROLMENT_CODE_INVALID deliberately never says WHY a code failed: malformed,
  // unknown, expired, used and other-organisation codes are indistinguishable.
  DEVICE_NOT_REGISTERED: msg({
    id: 'backendError.deviceNotRegistered',
    message: 'This device is not registered for child data. Ask your Kita admin to register it.',
  }),
  DEVICE_REVOKED: msg({
    id: 'backendError.deviceRevoked',
    message: 'This device was revoked. Ask your Kita admin to register it again.',
  }),
  ENROLMENT_CODE_INVALID: msg({
    id: 'backendError.deviceEnrolmentCodeInvalid',
    message: 'The code is not valid.',
  }),
  DEVICE_NOT_PENDING: msg({
    id: 'backendError.deviceNotPending',
    message: 'This device is already enrolled or revoked.',
  }),
  DEVICE_CODE_NOT_EXPIRED: msg({
    id: 'backendError.deviceCodeNotExpired',
    message: 'The current code of this device is still valid.',
  }),
  DEVICE_CHILDCARE_NOT_FOUND: msg({
    id: 'backendError.deviceChildcareNotFound',
    message: 'This location no longer exists. Choose another location.',
  }),
  DEVICE_NOT_FOUND: msg({
    id: 'backendError.deviceNotFound',
    message: 'This device no longer exists.',
  }),
  DEVICE_PLATFORM_INVALID: msg({
    id: 'backendError.devicePlatformInvalid',
    message: 'This app platform is not supported. Use the employee app on iOS or Android.',
  }),
  DEVICE_NAME_REQUIRED: msg({
    id: 'backendError.deviceNameRequired',
    message: 'Enter a device name: the location has no name to use.',
  }),
  ORGANISATION_NOT_FOUND: msg({
    id: 'backendError.organisationNotFound',
    message: 'Your organisation is not active. Contact support.',
  }),

  // Employee personal-data change requests (issue #1766).
  EMPLOYEE_DATA_REQUEST_OPEN: msg({
    id: 'backendError.employeeDataRequestOpen',
    message: 'A change request for this data is already open or being processed.',
  }),
  EMPLOYEE_DATA_REQUEST_NOT_OPEN: msg({
    id: 'backendError.employeeDataRequestNotOpen',
    message: 'This change request is no longer open.',
  }),
  EMPLOYEE_DATA_NO_CHANGE: msg({
    id: 'backendError.employeeDataNoChange',
    message: 'The request does not change anything.',
  }),
  EMPLOYEE_DATA_REQUEST_NOT_FOUND: msg({
    id: 'backendError.employeeDataRequestNotFound',
    message: 'Change request not found.',
  }),

  // Employee time corrections (issue #1979).
  TIME_CORRECTION_REQUEST_OPEN: msg({
    id: 'backendError.timeCorrectionRequestOpen',
    message: 'A correction of this day is already open or being processed.',
  }),
  TIME_CORRECTION_REQUEST_NOT_OPEN: msg({
    id: 'backendError.timeCorrectionRequestNotOpen',
    message: 'This correction request is no longer open.',
  }),
  TIME_CORRECTION_NO_CHANGE: msg({
    id: 'backendError.timeCorrectionNoChange',
    message: 'The correction does not change anything.',
  }),
  TIME_CORRECTION_REQUEST_NOT_FOUND: msg({
    id: 'backendError.timeCorrectionRequestNotFound',
    message: 'Correction request not found.',
  }),
  CHANGE_REQUEST_FAMILY_NOT_ALLOWED: msg({
    id: 'backendError.changeRequestFamilyNotAllowed',
    message: 'This change request type cannot be filed here.',
  }),
  CHANGE_REQUEST_REASON_REQUIRED: msg({
    id: 'backendError.changeRequestReasonRequired',
    message: 'Enter a reason for the employee.',
  }),

  // Staff-facing codes the map was missing (web admin UX pass).
  ADMIN_REQUIRED: msg({
    id: 'backendError.adminRequired',
    message: 'Only an administrator can change this. Ask an administrator of your organisation.',
  }),
  SHIFT_WEEKDAYS_REQUIRED: msg({
    id: 'backendError.shiftWeekdaysRequired',
    message: 'Select at least one weekday for the repeating shift.',
  }),
  ABSENCE_ALREADY_STARTED: msg({
    id: 'backendError.absenceAlreadyStarted',
    message: 'This absence has already started and can no longer be deleted.',
  }),
  ABSENCE_IN_THE_PAST: msg({
    id: 'backendError.absenceInThePast',
    message: 'The date of this absence has passed. Only the office can record past absences.',
  }),
  ABSENCE_SETTINGS_INVALID: msg({
    id: 'backendError.absenceSettingsInvalid',
    message: 'One of the absence settings is outside the allowed range. Check the values and save again.',
  }),
  SHIFT_SETTINGS_INVALID: msg({
    id: 'backendError.shiftSettingsInvalid',
    message: 'One of the shift settings is outside the allowed range. Check the values and save again.',
  }),
  PAUSE_OVERLAP: msg({
    id: 'backendError.pauseOverlap',
    message: 'This pause overlaps an existing pause. Choose dates outside the existing pause.',
  }),
  CHANGE_REQUEST_NOT_FOUND: msg({
    id: 'backendError.changeRequestNotFound',
    message: 'This change request no longer exists. Reload the page to see the current list.',
  }),
  CHANGE_REQUEST_NOT_EDITABLE: msg({
    id: 'backendError.changeRequestNotEditable',
    message: 'This change request has already been processed and can no longer be edited.',
  }),
  CHANGE_REQUEST_NOT_APPROVED: msg({
    id: 'backendError.changeRequestNotApproved',
    message: 'Approve this change request before applying it.',
  }),
  CHANGE_REQUEST_AUTO_STAGE_INELIGIBLE: msg({
    id: 'backendError.changeRequestAutoStageIneligible',
    message: 'This change type can be applied automatically only after its requests were approved without edits or rejections. Leave it off until then.',
  }),
  CONTRACT_TERMINATION_AFTER_END: msg({
    id: 'backendError.contractTerminationAfterEnd',
    message: 'The termination date is after the contract\'s end date. Choose a date on or before the end date.',
  }),
  SETTLING_IN_ALREADY_SIGNED_OFF: msg({
    id: 'backendError.settlingInAlreadySignedOff',
    message: 'This settling-in plan has already been signed off. Reload the page to see its current state.',
  }),
  ORG_CURRENCY_NOT_CONFIGURED: msg({
    id: 'backendError.orgCurrencyNotConfigured',
    message: 'Set the billing currency in the billing settings before recording a cash payment.',
  }),
  INTAKE_CHILDCARE_REQUIRED: msg({
    id: 'backendError.intakeChildcareRequired',
    message: 'Select at least one desired childcare location.',
  }),
  TOO_MANY_IMAGES: msg({
    id: 'backendError.tooManyImages',
    message: 'Too many images are attached. Remove some and save again.',
  }),
  INVALID_FILE_CONTENT: msg({
    id: 'backendError.invalidFileContent',
    message: 'The file\'s content does not match its file type. Upload a valid file.',
  }),
  INVALID_ATTACHMENT_FILENAME: msg({
    id: 'backendError.invalidAttachmentFilename',
    message: 'This file name is not allowed. Rename the file and upload it again.',
  }),
  UPLOAD_NOT_VERIFIABLE: msg({
    id: 'backendError.uploadNotVerifiable',
    message: 'The file could not be verified. Upload it again.',
  }),
  INVALID_BILLING_SETTINGS: msg({
    id: 'backendError.invalidBillingSettings',
    message: 'Check the billing settings: the BESR-ID needs exactly 6 digits, and the subsidy rounding threshold must be above 0 and within the allowed limit.',
  }),
  // #2004: billing_settings.subsidy_expiry_action = hold.
  // #2028: the late extras regenerate.
  LATE_EXTRAS_NOT_PENDING: msg({
    id: 'backendError.lateExtrasNotPending',
    message: 'This item was already re-issued or skipped. Reload the inbox.',
  }),
  LATE_EXTRAS_REGENERATING: msg({
    id: 'backendError.lateExtrasRegenerating',
    message: 'The invoices of this item are being re-issued. Try again in a few minutes.',
  }),
  SUBSIDY_EXPIRED_HOLD: msg({
    id: 'backendError.subsidyExpiredHold',
    message: 'The Beitragsfaktor or SBU confirmation of this contract expired before the billing month, and invoices are held until it is renewed. Enter the renewal, then bill again.',
  }),
  SUBSIDY_DECISION_PENDING: msg({
    id: 'backendError.subsidyDecisionPending',
    message: 'The subsidy office\'s decision is still pending. Enter the decision on the contract and clear the hold, then bill again.',
  }),
  INVOICE_REGENERATION_CREDITED: msg({
    id: 'backendError.invoiceRegenerationCredited',
    message: 'A period in this range has a credited invoice. Undo the credit note first, then bill again.',
  }),
  INVOICE_STATUS_DERIVED: msg({
    id: 'backendError.invoiceStatusDerived',
    message: 'The invoice status follows its payments and cannot be set by hand. Record or correct the payment instead.',
  }),
  FAMILY_GROUP_INVALID_BILLING_TYPE: msg({
    id: 'backendError.familyGroupInvalidBillingType',
    message: 'Choose how the family group is billed: one combined invoice or split billing.',
  }),
  FAMILY_GROUP_INVALID_SPLIT_PERCENTAGES: msg({
    id: 'backendError.familyGroupInvalidSplitPercentages',
    message: 'The split percentages must add up to 100%. Adjust them and save again.',
  }),
  CREDIT_TRANSFER_FAMILY_ONLY: msg({
    id: 'reconciliation.moveCredit.error.familyOnly',
    message: 'Credit moves only between two different family accounts.',
  }),
  CREDIT_TRANSFER_PAYMENT_NOT_PARKED: msg({
    // Every reason of the 409 (also not_parked, which the dialog's 'applied'
    // filter only lets through on stale data or a direct API call).
    id: 'reconciliation.moveCredit.error.changed',
    message: 'A selected payment has changed in the meantime. Reload the page and try again.',
  }),
  RECONCILIATION_SEARCH_QUERY_TOO_SHORT: msg({
    id: 'backendError.reconciliationSearchQueryTooShort',
    message: 'Enter at least 2 characters to search.',
  }),
  INVARIANT_VIOLATION: msg({
    id: 'backendError.invariantViolation',
    message: 'The amounts do not add up, so nothing was changed. Reload the page and try again. If it happens again, contact support.',
  }),

  // Dunning approval re-gate (#1948): nothing was sent, the letter was updated.
  amounts_changed: msg({
    id: 'backendError.dunningAmountsChanged',
    message: 'Nothing was sent: the amounts changed since the letter was prepared. Review the updated letter and approve it again.',
  }),
  // Dunning draft recipient (#1963) and paper letter (#2008).
  DUNNING_RECIPIENT_FROM_CONTACT: msg({
    id: 'backendError.dunningRecipientFromContact',
    message: 'Reminders go to the billing contact of the account. Change the address on the contact.',
  }),
  DUNNING_NO_LETTER_DUE: msg({
    id: 'backendError.dunningNoLetterDue',
    message: 'No letter is due for this household: its dunning is closed or no further stage is enabled.',
  }),
  // Subsidy documents on a contract version (#2002).
  SUBSIDY_DOCUMENT_CATEGORY_INVALID: msg({
    id: 'backendError.subsidyDocumentCategoryInvalid',
    message: 'This is not a subsidy document type. Choose one from the list.',
  }),
  SUBSIDY_DOCUMENT_VALIDITY_NOT_ALLOWED: msg({
    id: 'backendError.subsidyDocumentValidityNotAllowed',
    message: 'Only BF and SBU confirmations and KmbB papers have validity dates.',
  }),
  SUBSIDY_DOCUMENT_VALIDITY_ORDER: msg({
    id: 'backendError.subsidyDocumentValidityOrder',
    message: '"Valid from" must not be after "valid until".',
  }),
  SUBSIDY_DOCUMENT_FILE_TYPE: msg({
    id: 'backendError.subsidyDocumentFileType',
    message: 'Subsidy documents must be PDF, JPEG or PNG files.',
  }),
  DOCUMENT_CATEGORY_RESERVED: msg({
    id: 'backendError.documentCategoryReserved',
    message: 'This category is for subsidy documents. Upload them on the contract.',
  }),
  // Sozialdepartement queue (#2003).
  SUBSIDY_REPORT_ITEM_NOT_FOUND: msg({
    id: 'backendError.subsidyReportItemNotFound',
    message: 'This subsidy report no longer exists. Reload the list.',
  }),
  SUBSIDY_REPORT_EXISTS: msg({
    id: 'backendError.subsidyReportExists',
    message: 'An open item of this kind already exists for this contract version.',
  }),
  SUBSIDY_REPORT_NOT_ELIGIBLE: msg({
    id: 'backendError.subsidyReportNotEligible',
    message: 'Only subsidised contracts with a Beitragsfaktor office are reported to the Sozialdepartement.',
  }),
  // A kmbb item (#2007) needs the KmbB papers instead of the BF and SBU confirmations.
  SUBSIDY_REPORT_DOCUMENTS_MISSING: msg({
    id: 'backendError.subsidyReportPapersMissing',
    message: 'Attach the valid papers this request needs to the contract before marking it filed.',
  }),
  SUBSIDY_REPORT_DECISION_NUMBER_REQUIRED: msg({
    id: 'backendError.subsidyReportDecisionNumberRequired',
    message: 'Enter the decision number of the Sozialdepartement.',
  }),
  SUBSIDY_REPORT_DATE_IN_FUTURE: msg({
    id: 'backendError.subsidyReportDateInFuture',
    message: 'The date must not be in the future.',
  }),
  SUBSIDY_REPORT_BF_MISSING: msg({
    id: 'backendError.subsidyReportBfMissing',
    message: 'Enter the Beitragsfaktor from the decision on the contract first.',
  }),
  SUBSIDY_REPORT_CONTRACT_GONE: msg({
    id: 'backendError.subsidyReportContractGone',
    message: 'The contract version of this item no longer exists.',
  }),
};

export interface BackendErrorBody {
  message?: string;
  code?: string;
  errors?: Record<string, string>;
}

export function extractBackendError(error: unknown): BackendErrorBody {
  if (error && typeof error === 'object') {
    if ('body' in error) {
      const body = (error as { body?: BackendErrorBody }).body;
      if (body && typeof body === 'object') return body;
    }
    if ('code' in error && typeof (error as { code?: unknown }).code === 'string') {
      const e = error as { code: string; message?: string };
      return {
        code: e.code,
        message: e.message,
      };
    }
  }
  return {};
}

export function translateBackendError(
  code: string | undefined,
  i18n: I18n,
  fallback: MessageDescriptor
): string {
  if (code && codeToMessage[code]) {
    return i18n._(codeToMessage[code]);
  }
  return i18n._(fallback);
}

/**
 * backendErrorText renders the most useful text an error carries, in order:
 *
 *   1. the localised message for a code we know;
 *   2. the backend's own `message` — English, but SPECIFIC and usually an
 *      instruction ("Move that payment from the office's account first");
 *   3. the caller's generic fallback.
 *
 * Step 2 is the point: a handler that returns an actionable 400 must not be
 * flattened into "please try again", because retrying cannot work. Prefer this
 * over translateBackendError wherever the endpoint answers with instructions
 * rather than transient failures.
 */
export function backendErrorText(
  error: unknown,
  i18n: I18n,
  fallback: MessageDescriptor
): string {
  const { code, message } = extractBackendError(error);
  if (code && codeToMessage[code]) {
    return i18n._(codeToMessage[code]);
  }
  const raw = message?.trim();
  if (raw) {
    return raw;
  }
  return i18n._(fallback);
}

// The text HandleError sends for an error it does not recognise. It names no
// cause and no next step, so the caller's specific fallback says more.
const GENERIC_BACKEND_MESSAGE = 'An error occurred processing your request';

const offlineMessage = msg({
  id: 'common.error.offline',
  message: 'No connection to the server. Check your internet connection and try again.',
});

/**
 * True when the request never got an answer: the browser is offline, axios
 * reports ERR_NETWORK (no response), or `fetch` rejected with its TypeError.
 */
export function isNetworkError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; isAxiosError?: unknown; response?: unknown; name?: unknown; message?: unknown };
  if (e.code === 'ERR_NETWORK') return true;
  if (e.isAxiosError === true && e.response === undefined && e.code !== 'ERR_CANCELED') return true;
  return (
    e.name === 'TypeError' &&
    typeof e.message === 'string' &&
    /failed to fetch|networkerror|load failed/i.test(e.message)
  );
}

/** HTTP status carried by an ApiError, an axios error or a fetch-hook error. */
export function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof e.status === 'number') return e.status;
  if (typeof e.response?.status === 'number') return e.response.status;
  return undefined;
}

/**
 * errorToastText is the one way to turn a failed request into toast text:
 *
 *   1. no answer from the server: the offline message;
 *   2. a backend code we translate: its localised message;
 *   3. the client-safe `message` of a 4xx response body: specific, usually an
 *      instruction, but English;
 *   4. the caller's fallback.
 *
 * It never returns `error.message`. On an ApiError that is the status text
 * ("Bad Request") or, for 409/422/429, a "Generic Error ... body: {json}" dump;
 * on a plain Error it is whatever a developer wrote.
 */
export function errorToastText(
  error: unknown,
  i18n: I18n,
  fallback: MessageDescriptor | string
): string {
  if (isNetworkError(error)) return i18n._(offlineMessage);

  const { code } = extractBackendError(error);
  if (code && codeToMessage[code]) return i18n._(codeToMessage[code]);

  const status = errorStatus(error);
  const body =
    error && typeof error === 'object' && 'body' in error
      ? (error as { body?: unknown }).body
      : undefined;
  if (body && typeof body === 'object' && (status === undefined || status < 500)) {
    const raw = (body as BackendErrorBody).message;
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (text && text !== GENERIC_BACKEND_MESSAGE) return text;
  }

  return typeof fallback === 'string' ? fallback : i18n._(fallback);
}
